import { ChevronDown, ChevronRight, Eye } from 'lucide-react';

import { formatServerTimestamp } from '@/lib/timestamps';
import { cn } from '@/lib/utils';
import type { ChatSession } from '@/services/api';
import { OwnerBadge } from './OwnerBadge';
import type { TeamChatCollapseState, TeamChatGroup } from './teamChats';

export interface TeamChatOwnerOption {
  key: string;
  ownerUsername: string;
}

interface TeamChatsSectionProps {
  groups: TeamChatGroup[];
  owners: TeamChatOwnerOption[];
  ownerFilter: string;
  collapse: TeamChatCollapseState;
  activeSessionId: string | null;
  timeZone: string;
  onOwnerFilterChange: (ownerKey: string) => void;
  onToggleTeam: () => void;
  onToggleGroup: (groupKey: string) => void;
  onSelect: (sessionId: string) => void;
  renderDelete?: (session: ChatSession) => React.ReactNode;
}

/** Other users' chats in the tenant, read-only, grouped by owner. */
export function TeamChatsSection({
  groups,
  owners,
  ownerFilter,
  collapse,
  activeSessionId,
  timeZone,
  onOwnerFilterChange,
  onToggleTeam,
  onToggleGroup,
  onSelect,
  renderDelete,
}: TeamChatsSectionProps) {
  const TeamChevron = collapse.teamCollapsed ? ChevronRight : ChevronDown;
  return (
    <section aria-label="Team chats" className="mt-3 border-t border-surface-200 pt-2 dark:border-surface-800">
      <button
        type="button"
        onClick={onToggleTeam}
        aria-expanded={!collapse.teamCollapsed}
        className="flex w-full items-center gap-1 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-surface-500 hover:text-surface-800 dark:text-surface-400 dark:hover:text-surface-100"
      >
        <TeamChevron className="h-3.5 w-3.5" aria-hidden="true" />
        Team chats
      </button>
      {!collapse.teamCollapsed && (
        <div className="mt-1">
          <label className="mx-3 mb-2 flex items-center gap-2 text-xs text-surface-500 dark:text-surface-400">
            Person
            <select
              value={ownerFilter}
              onChange={(event) => onOwnerFilterChange(event.target.value)}
              className="min-w-0 flex-1 rounded-md border border-surface-200 bg-white px-2 py-1 text-xs text-surface-700 dark:border-surface-700 dark:bg-surface-900 dark:text-surface-200"
            >
              <option value="all">Everyone</option>
              {owners.map((owner) => (
                <option key={owner.key} value={owner.key}>{owner.ownerUsername}</option>
              ))}
            </select>
          </label>
          {groups.length === 0 && (
            <p className="px-3 py-1 text-xs text-surface-500 dark:text-surface-400">No team chats yet.</p>
          )}
          {groups.map((group) => {
            const groupCollapsed = collapse.collapsedGroups.includes(group.key);
            const GroupChevron = groupCollapsed ? ChevronRight : ChevronDown;
            return (
              <div key={group.key} data-team-group={group.key}>
                <button
                  type="button"
                  onClick={() => onToggleGroup(group.key)}
                  aria-expanded={!groupCollapsed}
                  className="flex w-full items-center gap-2 px-3 py-1 text-left text-sm text-surface-700 hover:bg-surface-100 dark:text-surface-300 dark:hover:bg-surface-800"
                >
                  <GroupChevron className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <OwnerBadge ownerUserId={group.ownerUserId} ownerUsername={group.ownerUsername} />
                  <span className="flex-1 truncate font-medium">{group.ownerUsername}</span>
                  <span className="text-xs text-surface-500 dark:text-surface-400">{group.sessions.length}</span>
                </button>
                {!groupCollapsed && group.sessions.map((session) => (
                  <div
                    key={session.id}
                    role="button"
                    tabIndex={0}
                    data-team-chat={session.id}
                    onClick={() => onSelect(session.id)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onSelect(session.id);
                      }
                    }}
                    className={cn(
                      'group mx-2 flex cursor-pointer items-center gap-2 rounded-lg py-2 pl-7 pr-3 text-left transition-colors',
                      session.id === activeSessionId
                        ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                        : 'text-surface-700 hover:bg-surface-100 dark:text-surface-300 dark:hover:bg-surface-800'
                    )}
                    style={{ width: 'calc(100% - 16px)' }}
                  >
                    <Eye className="h-4 w-4 shrink-0" aria-label="Read-only" />
                    <OwnerBadge ownerUserId={session.ownerUserId} ownerUsername={session.ownerUsername} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{session.name}</p>
                      <p className="truncate text-xs text-surface-500 dark:text-surface-400">
                        {session.messageCount} {session.messageCount === 1 ? 'message' : 'messages'} • {formatServerTimestamp(session.updatedAt, timeZone, { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                    {session.canDelete && renderDelete?.(session)}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
